/*
 * 内联 SVG 图标组件（等价于 XHeyClaw-Web 用的 lucide-react 对应图标）
 *
 * 所有图标：viewBox 24x24、stroke="currentColor"、fill="none"、strokeWidth=2、
 * strokeLinecap/Linejoin="round"，与 lucide 默认一致；尺寸由 CSS 控制。
 */

import React from 'react'

export interface IconProps {
  className?: string
  style?: React.CSSProperties
}

const baseProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
}

// Brain（lucide brain 简化版）
export const BrainIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
    <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
    <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
  </svg>
)

export const ChevronRightIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="m9 18 6-6-6-6" />
  </svg>
)

export const ChevronDownIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="m6 9 6 6 6-6" />
  </svg>
)

export const WrenchIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
)

/**
 * 流光环 loader — 淡紫细圆环作底, 一段紫色亮光带 (top/right border) 绕一圈
 * 旋转, 带 drop-shadow 发光. 视觉聚焦 + 通透不实心 + 亮带是亮点.
 *
 * 用 span + ::before 实现, 无子元素. 配色 / 动画在 ToolCallGroup.scss
 * 的 `.aiClerk-firefly` 部分 (类名保留, 避免外部引用断链).
 *
 * 速度可调: CSS 自定义属性 `--firefly-speed` (默认 1).
 *   <LoaderIcon style={{ '--firefly-speed': 0.5 } as any} />  // 2x 快
 *   <LoaderIcon style={{ '--firefly-speed': 1.5 } as any} />  // 1.5x 慢
 */
export const LoaderIcon: React.FC<IconProps> = (p) => (
  <span
    className={`aiClerk-firefly${p.className ? ' ' + p.className : ''}`}
    style={p.style}
    aria-hidden="true"
  />
)

export const CheckIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

export const XIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

export const FileTextIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
)

export const TerminalIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
)

export const SearchIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)

export const DatabaseIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
    <path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3" />
  </svg>
)

export const SparklesIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
    <path d="M5 3v4" />
    <path d="M19 17v4" />
    <path d="M3 5h4" />
    <path d="M17 19h4" />
  </svg>
)

export const GlobeIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    <path d="M2 12h20" />
  </svg>
)

export const PencilIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    <path d="m15 5 4 4" />
  </svg>
)

export const Trash2Icon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
)

export const MoreHorizontalIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
)

export const FileSpreadsheetIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M8 13h2" />
    <path d="M8 17h2" />
    <path d="M14 13h2" />
    <path d="M14 17h2" />
  </svg>
)

export const FolderOpenIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.55 6a2 2 0 0 1-1.94 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
  </svg>
)

export const UsersIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

export const ImageIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
)

export const DownloadIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

export const PaperclipIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
)

// 发送按钮箭头 — lucide arrow-up 简化版,跟 PaperclipIcon 同 viewBox + stroke 风格,
// 用 SVG 替代原来的 ↑ 字符,可对齐 attach 按钮的视觉尺寸(同样由 CSS 18×18 控制).
export const ArrowUpIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M12 19V5" />
    <path d="m5 12 7-7 7 7" />
  </svg>
)

export const BrushIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" />
    <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z" />
  </svg>
)

export const CalendarIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

export const ServerIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
    <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
)

export const MessageSquareIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)

export const TagIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
)

export const RefreshCwIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
)

export const CopyIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
)

export const ThumbsUpIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M7 10v12" />
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7V10l4.76-9.52a1 1 0 0 1 1.79.03L15 5.88Z" />
  </svg>
)

export const ThumbsDownIcon: React.FC<IconProps> = (p) => (
  <svg {...baseProps} {...p}>
    <path d="M17 14V2" />
    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17v12l-4.76 9.52a1 1 0 0 1-1.79-.03L9 18.12Z" />
  </svg>
)
